import * as React from 'react';

import { getApiBaseUrl } from '../lib/getEnv';

// Generate a unique ID for messages and tool executions
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// WebSocket Message Types
// ============================================

interface IStatusUpdate {
  type: 'status';
  status: 'thinking' | 'executing_tool' | 'complete' | 'error';
  toolName?: string;
  toolDescription?: string;
}

interface IToolStarted {
  type: 'tool_started';
  toolId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
}

interface IToolCompleted {
  type: 'tool_completed';
  toolId: string;
  toolName: string;
  resultSummary: string;
}

interface IAssistantMessage {
  type: 'assistant_message';
  content: string;
}

interface IErrorMessage {
  type: 'error';
  message: string;
  code?: string;
}

type ServerMessage = IStatusUpdate | IToolStarted | IToolCompleted | IAssistantMessage | IErrorMessage;

// ============================================
// Chat Message Types for UI
// ============================================

export interface IChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolExecutions?: IToolExecution[];
}

export interface IToolStatus {
  status: 'thinking' | 'executing_tool' | 'complete' | 'error';
  toolName?: string;
  toolDescription?: string;
}

export interface IToolExecution {
  id: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  resultSummary?: string;
  status: 'running' | 'complete' | 'error';
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// ============================================
// Hook Return Type
// ============================================

export interface IUseChatWebSocketReturn {
  messages: IChatMessage[];
  toolStatus: IToolStatus | null;
  toolExecutions: IToolExecution[];
  isProcessing: boolean;
  connectionStatus: ConnectionStatus;
  error: string | null;
  sendMessage: (content: string) => void;
  clearMessages: () => void;
}

/**
 * Custom hook for managing WebSocket connection to the chat server.
 * Handles authentication, message sending, and real-time status updates.
 */
export const useChatWebSocket = (sessionToken: string | null): IUseChatWebSocketReturn => {
  const [messages, setMessages] = React.useState<IChatMessage[]>([]);
  const [toolStatus, setToolStatus] = React.useState<IToolStatus | null>(null);
  const [toolExecutions, setToolExecutions] = React.useState<IToolExecution[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>('disconnected');
  const [error, setError] = React.useState<string | null>(null);

  const wsRef = React.useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingMessageRef = React.useRef<string | null>(null);
  const isConnectingRef = React.useRef(false);
  const hasConnectedRef = React.useRef(false);

  // Track current tool executions for the current request
  const currentToolExecutionsRef = React.useRef<IToolExecution[]>([]);

  // Handle incoming server messages - use ref to avoid recreating
  const handleServerMessageRef = React.useRef<((data: ServerMessage) => void) | undefined>(undefined);
  handleServerMessageRef.current = (data: ServerMessage) => {
    switch (data.type) {
      case 'status':
        setToolStatus({
          status: data.status,
          toolName: data.toolName,
          toolDescription: data.toolDescription,
        });

        if (data.status === 'thinking') {
          setIsProcessing(true);
        } else if (data.status === 'complete') {
          setTimeout(() => setToolStatus(null), 500);
        } else if (data.status === 'error') {
          setToolStatus(null);
          setIsProcessing(false);
        }
        break;

      case 'tool_started': {
        const newTool: IToolExecution = {
          id: data.toolId,
          toolName: data.toolName,
          toolInput: data.toolInput,
          status: 'running' as const,
        };
        currentToolExecutionsRef.current = [...currentToolExecutionsRef.current, newTool];
        setToolExecutions([...currentToolExecutionsRef.current]);
        break;
      }

      case 'tool_completed': {
        const idx = currentToolExecutionsRef.current.findIndex((t) => t.id === data.toolId && t.status === 'running');
        if (idx >= 0) {
          const next = [...currentToolExecutionsRef.current];
          next[idx] = {
            ...next[idx],
            status: 'complete' as const,
            resultSummary: data.resultSummary,
          };
          currentToolExecutionsRef.current = next;
          setToolExecutions(next);
        }
        break;
      }

      case 'assistant_message': {
        // Store tool executions with the message for history
        const toolsForMessage =
          currentToolExecutionsRef.current.length > 0 ? [...currentToolExecutionsRef.current] : undefined;
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-${generateId()}`,
            role: 'assistant',
            content: data.content,
            timestamp: new Date(),
            toolExecutions: toolsForMessage,
          },
        ]);
        setToolStatus(null);
        setIsProcessing(false);
        // Clear current tool executions for next request
        currentToolExecutionsRef.current = [];
        setToolExecutions([]);
        break;
      }

      case 'error':
        setError(data.message);
        setToolStatus(null);
        setIsProcessing(false);
        // Clear tool executions on error
        currentToolExecutionsRef.current = [];
        setToolExecutions([]);
        break;

      default:
        break;
    }
  };

  // Connect to WebSocket - stable reference using ref for reconnect
  const connectRef = React.useRef<(() => void) | undefined>(undefined);
  connectRef.current = () => {
    if (!sessionToken || isConnectingRef.current) {
      if (!sessionToken) {
        setConnectionStatus('disconnected');
      }
      return;
    }

    // Prevent multiple simultaneous connection attempts
    isConnectingRef.current = true;

    // Close existing connection
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    setConnectionStatus('connecting');
    setError(null);

    try {
      // Derive the WebSocket origin from the API base URL when it's set explicitly,
      // or from `window.location` when the build targets same-origin (CloudFront deploys
      // set `VITE_API_SERVER_URL=''` so all API calls become relative).
      const apiUrl = getApiBaseUrl();
      let wsOrigin: string;
      if (apiUrl) {
        const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
        wsOrigin = `${wsProtocol}://${apiUrl.replace(/^https?:\/\//, '')}`;
      } else {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        wsOrigin = `${wsProtocol}://${window.location.host}`;
      }
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneParam = timezone ? `&timezone=${encodeURIComponent(timezone)}` : '';
      const wsUrl = `${wsOrigin}/chat?token=${sessionToken}${timezoneParam}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        hasConnectedRef.current = true;
        setConnectionStatus('connected');
        setError(null);

        if (pendingMessageRef.current) {
          const message = pendingMessageRef.current;
          pendingMessageRef.current = null;
          ws.send(JSON.stringify({ type: 'chat_message', content: message }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as ServerMessage;
          handleServerMessageRef.current?.(data);
        } catch {
          // Silently ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        setConnectionStatus('disconnected');
        setIsProcessing(false);
        setToolStatus(null);
        currentToolExecutionsRef.current = [];
        setToolExecutions([]);

        if (event.code === 4001) {
          setError('Authentication failed');
        } else if (event.code === 4003) {
          setError('Admin access required');
        } else if (event.code !== 1000) {
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current?.();
          }, 3000);
        }
      };

      ws.onerror = () => {
        isConnectingRef.current = false;
        // Only show error if we had a connection that dropped - not during initial connection
        if (hasConnectedRef.current) {
          setConnectionStatus('error');
          setError('Connection error');
        }
      };
    } catch (err) {
      isConnectingRef.current = false;
      setConnectionStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  // Stable connect function that doesn't change
  const connect = React.useCallback(() => {
    connectRef.current?.();
  }, []);

  // Send a message to the server
  const sendMessage = React.useCallback(
    (content: string) => {
      if (!content.trim()) {
        return;
      }

      // Add user message to local state immediately
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${generateId()}`,
          role: 'user',
          content,
          timestamp: new Date(),
        },
      ]);

      // Clear any previous error and tool executions for new request
      setError(null);
      currentToolExecutionsRef.current = [];
      setToolExecutions([]);
      setIsProcessing(true);

      // Send to WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'chat_message', content }));
      } else {
        // Queue message and attempt reconnect
        pendingMessageRef.current = content;
        connect();
      }
    },
    [connect]
  );

  // Clear all messages
  const clearMessages = React.useCallback(() => {
    setMessages([]);
    setToolStatus(null);
    currentToolExecutionsRef.current = [];
    setToolExecutions([]);
    setIsProcessing(false);
    setError(null);
  }, []);

  // Connect on mount and when session token changes
  React.useEffect(() => {
    if (sessionToken) {
      connectRef.current?.();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [sessionToken]);

  return {
    messages,
    toolStatus,
    toolExecutions,
    isProcessing,
    connectionStatus,
    error,
    sendMessage,
    clearMessages,
  };
};
